/**
 * `useAccountQuery` - TanStack Query wrapper around `BffClient.getAccount`.
 *
 * Single account fetch keyed by the `id` path param. 404 surfaces as a
 * `BffError` with `status=404` that the detail page renders as a
 * `NotFoundState`; 401/403 fall through to the shared error provider.
 *
 * `staleTime` is set short (30s) - the detail page is read-only and the
 * underlying account row updates infrequently, but we never want to
 * serve a truly stale projection to a CSA reading values out to a caller.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query'

import type { Account, BffClient } from '../../../lib/bff-client'

export interface UseAccountQueryArgs {
  readonly client: BffClient
  readonly accountId: string
}

export function accountQueryKey(accountId: string): readonly unknown[] {
  return ['account', accountId] as const
}

export function useAccountQuery({
  client,
  accountId,
}: UseAccountQueryArgs): UseQueryResult<Account, Error> {
  return useQuery({
    queryKey: accountQueryKey(accountId),
    queryFn: () => client.getAccount(accountId),
    staleTime: 30_000,
    retry: (failureCount, err) => {
      const status = (err as { status?: number }).status
      if (status === 401 || status === 403 || status === 400 || status === 404) {
        return false
      }
      return failureCount < 1
    },
    throwOnError: false,
  })
}
